import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { ICarnet } from "../../domain/ICarnet";
import { ICarentsTotals } from "../../domain/ICarnetsTotals";
import { IDeleteCarnetExtendedRequestParams } from "../../domain/IDeleteCarnetExtendedRequestParams";
import { IGetCarnetsRequestParams } from "../../domain/IGetCarnetsRequestParams";
import { carnetService } from "../../services/carnet.service";
import { IPopoverState } from "../../domain/IPopoverState";
import { FormatDateIso, getFristDateOfCurrnetMonth } from "utils/dateUtils";
import { IHtzEntry } from "../../domain/IHtzEntry";
import { IGetHtzEntriesRequestParams } from "../../domain/IGtHtzEntriesRequestParams";
import { createCarnets } from "../Main/main.slice";

export const getCreatedCarnetsInPeriod = createAsyncThunk(
  "Carnets/getCreatedCarnetsInPeriod",
  async (data: IGetCarnetsRequestParams) => {
    try {
      return await carnetService.getCarnetsInPeriod(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteCarnet = createAsyncThunk(
  "Carnets/deleteCarnet",
  async (data: IDeleteCarnetExtendedRequestParams, { rejectWithValue }) => {
    try {
      return await carnetService.deleteCarnet({ carnetId: data.carnetId });
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const getHtzEntries = createAsyncThunk(
  "Carnets/getHtzEntries",
  async (data: IGetHtzEntriesRequestParams) => {
    try {
      return await carnetService.getHtzEntries(data);
    } catch (err) {
      setError(err);
    }
  }
);

interface carnetsState {
  loading: boolean;
  carnets: ICarnet[];
  totals: ICarentsTotals;
  selectedDateFrom: string | undefined;
  selectedDateTo: string | undefined;
  selectedEmployeeId: number | undefined;
  selectedProjectId: number | undefined;
  htzEntries: IHtzEntry[];
  deleteCarnetModalOpen: boolean;
  noteForCarnetPopover: IPopoverState;
  indexForCarnet: number;
  indexForDeletion: number;
  error: any;
}

const initialState: carnetsState = {
  loading: false,
  carnets: [],
  totals: {
    hoursTotals: [],
    effectiveKilometers: 0,
    effectiveHours: 0,
    regularBreakfastsTotal: 0,
    regularLunchesTotal: 0,
    regularDinnersTotal: 0,
    cashBreakfastsTotal: 0,
    cashLunchesTotal: 0,
    cashDinnersTotal: 0,
    sickLeavesTotal: 0,
    vacationsTotal: 0,
  },
  selectedDateFrom: FormatDateIso(getFristDateOfCurrnetMonth()),
  selectedDateTo: FormatDateIso(new Date()),
  selectedEmployeeId: undefined,
  selectedProjectId: undefined,
  htzEntries: [],
  deleteCarnetModalOpen: false,
  noteForCarnetPopover: {
    isOpen: false,
    top: 0,
    left: 0,
  },
  indexForCarnet: -1,
  indexForDeletion: -1,
  error: null,
};

export const carnetsSlice = createSlice({
  name: "carnets-carnets",
  initialState: initialState,
  reducers: {
    setSelectedDates: (
      state,
      action: PayloadAction<{
        dateFrom: string | undefined; dateTo: string | undefined
      }>
    ) => {
      const { dateFrom, dateTo } = action.payload;
      state.selectedDateFrom = dateFrom;
      state.selectedDateTo = dateTo;
    },
    setSelectedEmployeeId: (
      state,
      action: PayloadAction<{ employeeId: number | undefined }>
    ) => {
      state.selectedEmployeeId = action.payload.employeeId;
    },
    setSelectedProjectId: (
      state,
      action: PayloadAction<{ projectId: number | undefined }>
    ) => {
      state.selectedProjectId = action.payload.projectId;
    },
    setDeleteCarnetModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteCarnetModalOpen = action.payload.visible;
      state.indexForDeletion = action.payload.index;
    },
    setNoteForCarnetPopover: (
      state,
      action: PayloadAction<{ popoverState: IPopoverState, index: number }>
    ) => {
      state.noteForCarnetPopover = action.payload.popoverState;
      state.indexForCarnet = action.payload.index;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    updateCarnet: (
      state,
      action: PayloadAction<{ index: number; carnet: ICarnet }>
    ) => {
      state.carnets[action.payload.index] = action.payload.carnet;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCreatedCarnetsInPeriod.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getCreatedCarnetsInPeriod.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        const { carnets, ...totals } = action.payload;
        state.carnets = carnets;
        state.totals = totals;
      }
    });
    builder.addCase(getCreatedCarnetsInPeriod.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getHtzEntries.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getHtzEntries.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.htzEntries = action.payload.htzEntries;
      }
    });
    builder.addCase(getHtzEntries.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(createCarnets.fulfilled, (state, action) => {
      if (action.payload) {
        const addedOrChangedCarnetIds = action.payload.carnets.map(carnet => carnet.carnetId);
        const deletedCarnetIds = action.payload.deletedCarnetIds;
        const unchangedOldCarnets = state.carnets
          .filter(carnet => !addedOrChangedCarnetIds.includes(carnet.carnetId)
            && !deletedCarnetIds.includes(carnet.carnetId));
        state.carnets = [
          ...action.payload.carnets,
          ...unchangedOldCarnets
        ];
      }
    });
    builder.addCase(deleteCarnet.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteCarnet.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        const deletedCarnetId = action.payload.carnetId;
        state.carnets = state.carnets.filter(carnet => carnet.carnetId !== deletedCarnetId);
      }
    });
    builder.addCase(deleteCarnet.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
  },
});

export const {
  setSelectedDates,
  setSelectedEmployeeId,
  setSelectedProjectId,
  setDeleteCarnetModalOpen,
  updateCarnet,
  setNoteForCarnetPopover,
  setError,
} = carnetsSlice.actions;

export const selectState = (state: RootState) => state.carnets.carnets;
export const selectCarnet = (state: RootState) =>
  state.carnets.carnets.carnets[
  state.carnets.carnets.indexForCarnet
  ];
export const selectCarnetForDeletion = (state: RootState) =>
  state.carnets.carnets.carnets[state.carnets.carnets.indexForDeletion];
export default carnetsSlice.reducer;
